import {
  AlertTriangle,
  CircleAlert,
  Globe,
  HardDrive,
  KeyRound,
  Link2,
  Lock,
  Plug,
  RefreshCw,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { type FormEvent, useCallback, useId, useReducer, useState } from "react";
import { ApiError } from "../../api/client.js";
import {
  type ConnectionTestInput,
  type ConnectionTestResult,
  isOwnerRequired,
  modelApi,
  oauthAuthorizePath,
  type ProviderListing,
  type ProviderView,
} from "../../api/model.js";
import { EmptyState, InlineError, LoadingTable } from "../../components/AsyncState.js";
import { Badge } from "../../components/Badge.js";
import { useApiResource } from "../../hooks/useApiResource.js";
import { formatDateTime, titleCase } from "../../lib/format.js";

/**
 * Provider settings (Task 11) -- the console surface for `apps/api/src/model-routes.ts`.
 *
 * ## Secrets are write-only here, structurally
 *
 * The credential travels in exactly one direction. It is typed into a
 * `type="password"` field, held in this module's own reducer state, handed to
 * `PUT /api/v1/model/providers/:id`, and cleared. Nothing renders it back,
 * because there is nothing to render: the API has no route and no field that
 * returns a secret or a ciphertext, so a configured provider is identified by
 * its **fingerprint** -- derived from ciphertext, never from plaintext -- plus
 * the date it was configured. That pairing is what the API returns for exactly
 * this purpose, and it is what this surface shows.
 *
 * The secret is retained on a *failed* save and cleared only on a successful
 * one. A 503 from a deployment with no envelope key is not something the
 * operator can fix by retyping an eighty-character key, and clearing the field
 * would make them do it anyway to retry a request that would fail identically.
 *
 * ## Egress is the first thing on every card, and it is not a tooltip
 *
 * The twelve providers are rendered in two groups under their own headings --
 * one local, eleven remote -- and every card leads with a full-width flag
 * naming what happens to the customer's source code and the host it would be
 * sent to. Ollama is the only `egress: "local"` provider and it is the flagship
 * zero-egress path; for a buyer who cannot let source leave their boundary it
 * is the entire proposition, and a buyer who cannot tell it apart from the other
 * eleven at a glance has not been told anything. The grouping, the flag, the
 * colour and the named host are four independent signals on purpose: a `title`
 * attribute is none of them, because it is invisible until hovered and absent
 * on touch entirely.
 *
 * ## Model id and base URL live here and nowhere else
 *
 * Both are per-request on the test route and there is no column for either --
 * `model_credentials` holds custody, not preferences (Task 9's note). So they
 * are local component state, typed per test, discarded with the page, and the
 * form says so. Persisting them in the browser was considered and rejected:
 * `baseUrl` decides which host the customer's source would be sent to, and a
 * browser-side store for that would be a security-relevant setting with no
 * server-side record of who set it or when. If either ever needs to persist, it
 * needs a column and an audit trail, not `localStorage`.
 */

/**
 * Vendor-facing names for the registry's ids.
 *
 * The registry deliberately holds ids and endpoints rather than product names,
 * so the console supplies them. An id absent from this table falls back to its
 * own title-cased form, which means a thirteenth provider renders correctly the
 * day it is added and reads slightly worse until someone names it -- the right
 * way round for a table that is presentation only.
 */
const PROVIDER_LABELS: Readonly<Record<string, string>> = Object.freeze({
  anthropic: "Claude (API key)",
  "anthropic-oauth": "Claude (host OAuth)",
  bedrock: "Amazon Bedrock",
  deepseek: "DeepSeek",
  gemini: "Google Gemini",
  glm: "GLM · Z.ai",
  grok: "Grok · xAI",
  kimi: "Kimi · Moonshot",
  ollama: "Ollama",
  openai: "OpenAI (API key)",
  "openai-oauth": "OpenAI (host OAuth)",
  openrouter: "OpenRouter",
});

function providerLabel(id: string): string {
  return Object.hasOwn(PROVIDER_LABELS, id)
    ? (PROVIDER_LABELS[id] ?? titleCase(id))
    : titleCase(id);
}

/**
 * The host a provider's traffic would reach, for the egress flag.
 *
 * Falls back to the raw string rather than throwing: the value is the registry's
 * own base URL and has already been validated server-side, but a surface whose
 * job is to say where source code goes must not be the thing that crashes when
 * it cannot parse one.
 */
function baseUrlHost(baseUrl: string): string {
  try {
    return new URL(baseUrl).host;
  } catch {
    return baseUrl;
  }
}

// --- the secret form's state ------------------------------------------------

export interface SecretFormState {
  secret: string;
  submitting: boolean;
  notice: string | null;
  error: string | null;
}

export type SecretFormAction =
  | { kind: "edit"; secret: string }
  | { kind: "submitting" }
  | { kind: "saved"; outcome: "configured" | "rotated" }
  | { kind: "failed"; message: string };

export const emptySecretForm: SecretFormState = Object.freeze({
  secret: "",
  submitting: false,
  notice: null,
  error: null,
});

/**
 * The whole lifetime of a credential inside the console.
 *
 * A reducer rather than three `useState`s so that "cleared on save, retained on
 * failure" is one pure function that can be asserted directly, instead of a
 * `setSecret("")` buried in a promise chain that only an interaction test could
 * reach.
 */
export function secretFormReducer(
  state: SecretFormState,
  action: SecretFormAction,
): SecretFormState {
  switch (action.kind) {
    case "edit":
      return { ...state, secret: action.secret, error: null, notice: null };
    case "submitting":
      return { ...state, submitting: true, error: null, notice: null };
    case "saved":
      // The one line that matters: the credential leaves this state the moment
      // the server has it, so it is not sitting in a DOM node afterwards.
      return {
        secret: "",
        submitting: false,
        notice:
          action.outcome === "rotated"
            ? "Key rotated. The previous material is gone and cannot be recovered."
            : "Key stored. It is encrypted at rest and cannot be read back.",
        error: null,
      };
    case "failed":
      return { ...state, submitting: false, error: action.message, notice: null };
  }
}

// --- actions ----------------------------------------------------------------

/**
 * The result of a save, carrying the server's own `outcome` rather than an
 * inference drawn from the status the console last read.
 *
 * `set` is insert-only and the route falls back to `rotate`, so the server is
 * the only party that knows which happened. A console that guessed from a
 * possibly stale listing would tell an operator "key stored" in exactly the case
 * that matters most -- another owner configured the provider between page load
 * and submit, and this save replaced their live material.
 */
export type SaveResult =
  | { kind: "saved"; outcome: "configured" | "rotated" }
  | { kind: "failed"; message: string };

export interface ProviderActions {
  /** Resolves to the result rather than throwing, because the card branches on it. */
  save: (providerId: string, secret: string) => Promise<SaveResult>;
  revoke: (providerId: string) => Promise<void>;
  test: (providerId: string, input: ConnectionTestInput) => Promise<ConnectionTestResult>;
  connectOAuth: (providerId: string) => void;
  refresh: () => void;
}

/**
 * What to tell an operator whose save failed, from the status alone.
 *
 * The route deliberately never echoes a rejected body, because on this route
 * the rejected body *is* the secret. The status code is not the body, though,
 * and it carries the one distinction the operator has to act on: a malformed
 * key is fixed by pasting a correct one, and a deployment with no envelope key
 * is not fixed by retyping anything. Collapsing both into "could not be stored"
 * would leave them unable to tell which.
 */
export function saveFailureMessage(reason: unknown): string {
  if (reason instanceof ApiError && reason.status === 400) {
    return "That key was rejected as malformed. Nothing was stored.";
  }
  if (reason instanceof ApiError && reason.status === 503) {
    return "Credential storage is unavailable on this deployment, so nothing was stored. Retyping the key will not help — an operator must configure an envelope key.";
  }
  return "The key could not be stored. Nothing was changed.";
}

function errorMessage(reason: unknown, fallback: string): string {
  return reason instanceof Error ? reason.message : fallback;
}

// --- presentation -----------------------------------------------------------

/**
 * What happens to the customer's source code if this provider is used.
 *
 * Rendered as content, with an icon, a headline and the destination host --
 * never as a `title`. The local variant is the only one that can claim nothing
 * leaves the host, and the registry enforces that claim rather than trusting it:
 * a `local` provider whose base URL is not loopback fails to resolve at all.
 */
export function EgressFlag({ provider }: { provider: ProviderView }) {
  const host = baseUrlHost(provider.baseUrl);
  if (provider.egress === "local") {
    return (
      <p className="egress-flag egress-flag--local">
        <HardDrive aria-hidden="true" size={16} />
        <strong>No egress</strong>
        <span>
          Runs on <code>{host}</code>. Your source code never leaves this host.
        </span>
      </p>
    );
  }
  return (
    <p className="egress-flag egress-flag--remote">
      <Globe aria-hidden="true" size={16} />
      <strong>Leaves your boundary</strong>
      <span>
        Using this provider sends your source code to <code>{host}</code>, a third party.
      </span>
    </p>
  );
}

function CustodyFacts({ provider }: { provider: ProviderView }) {
  if (provider.authMode === "none") {
    return <p className="provider-card__custody muted-copy">No credential required.</p>;
  }
  if (provider.status === "unconfigured") {
    return <p className="provider-card__custody muted-copy">No credential stored.</p>;
  }
  return (
    <dl className="provider-card__custody">
      <div>
        <dt>Fingerprint</dt>
        <dd>
          <code>{provider.fingerprint ?? "—"}</code>
        </dd>
      </div>
      <div>
        <dt>Configured</dt>
        <dd>{formatDateTime(provider.createdAt)}</dd>
      </div>
      {provider.rotatedAt ? (
        <div>
          <dt>Rotated</dt>
          <dd>{formatDateTime(provider.rotatedAt)}</dd>
        </div>
      ) : null}
      {provider.revokedAt ? (
        <div>
          <dt>Revoked</dt>
          <dd>{formatDateTime(provider.revokedAt)}</dd>
        </div>
      ) : null}
    </dl>
  );
}

function StatusBadge({ provider }: { provider: ProviderView }) {
  if (provider.authMode === "none") return <Badge label="Available locally" tone="positive" />;
  if (provider.status === "active") return <Badge label="Configured" tone="positive" />;
  if (provider.status === "revoked") return <Badge label="Revoked" tone="critical" />;
  return <Badge label="Not configured" tone="muted" />;
}

/**
 * The panel a reviewer or a viewer sees.
 *
 * Not an error alert. Every provider route is owner-gated, reads included, and
 * a 403 here is the system working: the surface decides whether this product may
 * transmit the customer's source code to a third party at all and takes custody
 * of a live vendor credential, which is a decision about the tenant's
 * relationship with an outside vendor rather than about a finding. So it
 * explains the role and offers no retry, because retrying cannot change it.
 */
export function OwnerRequiredPanel() {
  return (
    <div className="owner-required" role="note">
      <Lock aria-hidden="true" size={22} />
      <strong>Owner access is required for model providers</strong>
      <p>
        These settings decide whether Provenance may send your source code to a third party, which
        company that is, and they hold a live vendor credential your organisation is billed for.
        Only an owner of this tenant can view or change them.
      </p>
      <p className="muted-copy">
        Findings, scans and remediation are unaffected — nothing on this page is required for a
        verdict. Ask an owner of this tenant if a provider needs to be configured.
      </p>
    </div>
  );
}

// --- one provider -----------------------------------------------------------

/**
 * A provider that can never accept a credential, and why.
 *
 * `oauth.supported: false` is the honest answer for a vendor that publishes no
 * host-OAuth flow. Anthropic documents exactly three ways to authenticate the
 * Claude API — API key, Workload Identity Federation and App Attest — and none
 * is host OAuth, so `anthropic-oauth` refuses at configuration time by design.
 * Offering a key field here would store material that fails on every subsequent
 * call, which from this console is indistinguishable from the provider being
 * down: the phantom outage the whole package exists to avoid. So the card offers
 * nothing to configure and says so.
 */
function UnsupportedOAuthNotice({ provider }: { provider: ProviderView }) {
  const sibling = provider.id.replace(/-oauth$/, "");
  return (
    <div className="provider-card__unsupported" role="note">
      <AlertTriangle aria-hidden="true" size={16} />
      <div>
        <strong>Not supported by this provider</strong>
        <p>
          This vendor publishes no host OAuth flow, so there is nothing for Provenance to connect to.
          The API is authenticated with an API key, Workload Identity Federation or App Attest
          instead.
        </p>
        <p className="muted-copy">
          Use <strong>{providerLabel(sibling)}</strong> above to configure this vendor.
        </p>
      </div>
    </div>
  );
}

export function ProviderCard({
  provider,
  keyConfigured,
  confirmingRevoke,
  onArmRevoke,
  onCancelRevoke,
  actions,
  initialSecretForm,
}: {
  provider: ProviderView;
  keyConfigured: boolean;
  confirmingRevoke: boolean;
  onArmRevoke: () => void;
  onCancelRevoke: () => void;
  actions: ProviderActions;
  /** Test seam: lets the credential form be rendered in a state a static render cannot reach. */
  initialSecretForm?: SecretFormState;
}) {
  const secretId = useId();
  const modelId = useId();
  const baseUrlId = useId();
  const [form, dispatch] = useReducer(secretFormReducer, initialSecretForm ?? emptySecretForm);
  // Per-test configuration. Deliberately not persisted -- see the file header.
  const [model, setModel] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<ConnectionTestResult | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const oauthProvider = provider.authMode === "oauth";
  const oauthSupported = provider.oauth?.supported === true;
  const oauthConnectable = oauthSupported && provider.oauth?.configured === true && keyConfigured;
  // A provider with no host-OAuth flow accepts no credential at all, and one
  // that authenticates by OAuth is connected rather than typed into.
  const acceptsSecret = provider.authMode === "api-key" || provider.authMode === "bearer";
  const credentialless = provider.authMode === "none";
  const testable = credentialless || provider.status === "active";

  const submitSecret = async (event: FormEvent) => {
    event.preventDefault();
    if (form.secret.length === 0) return;
    dispatch({ kind: "submitting" });
    const result = await actions.save(provider.id, form.secret);
    // The reducer decides whether the field is cleared; the result decides which
    // way. Nothing else in this component touches `form.secret`, and the
    // `configured`/`rotated` wording comes from the server rather than from this
    // component's possibly stale view of the slot.
    if (result.kind === "saved") {
      dispatch({ kind: "saved", outcome: result.outcome });
    } else {
      dispatch({ kind: "failed", message: result.message });
    }
  };

  const runTest = async () => {
    setTesting(true);
    setActionError(null);
    setTestResult(null);
    try {
      setTestResult(
        await actions.test(provider.id, {
          model: model.trim(),
          baseUrl: baseUrl.trim() === "" ? undefined : baseUrl.trim(),
        }),
      );
    } catch (reason) {
      setActionError(errorMessage(reason, "The connection could not be tested."));
    } finally {
      setTesting(false);
    }
  };

  const confirmRevoke = async () => {
    setActionError(null);
    try {
      await actions.revoke(provider.id);
      onCancelRevoke();
    } catch (reason) {
      setActionError(errorMessage(reason, "The credential could not be revoked."));
    }
  };

  return (
    <article
      className={`provider-card provider-card--${provider.egress}`}
      aria-labelledby={`provider-${provider.id}`}
    >
      <header className="provider-card__header">
        <div>
          <h3 id={`provider-${provider.id}`}>{providerLabel(provider.id)}</h3>
          <small className="cell-secondary">
            <code>{provider.id}</code> · {provider.transport}
          </small>
        </div>
        <StatusBadge provider={provider} />
      </header>

      <EgressFlag provider={provider} />
      <CustodyFacts provider={provider} />

      {provider.status === "revoked" ? (
        <p className="provider-card__note muted-copy">
          This credential was revoked and the stored secret is gone. The only way back is supplying
          different material below.
        </p>
      ) : null}

      {credentialless ? (
        <p className="provider-card__note">
          <ShieldCheck aria-hidden="true" size={15} />
          No credential required. This provider is usable on a deployment that has configured
          nothing at all.
        </p>
      ) : null}

      {oauthProvider && !oauthSupported ? <UnsupportedOAuthNotice provider={provider} /> : null}

      {oauthProvider && oauthSupported ? (
        <div className="provider-card__oauth">
          <button
            className="button button--primary"
            type="button"
            disabled={!oauthConnectable}
            onClick={() => actions.connectOAuth(provider.id)}
          >
            <Link2 aria-hidden="true" size={15} />
            {provider.status === "active" ? "Reconnect" : "Connect"}
          </button>
          {provider.oauth?.configured === false ? (
            <small className="cell-secondary">
              This deployment has registered no OAuth client for {providerLabel(provider.id)}.
            </small>
          ) : null}
        </div>
      ) : null}

      {acceptsSecret ? (
        <form className="provider-card__secret" onSubmit={submitSecret}>
          <label className="form-field" htmlFor={secretId}>
            <span>{provider.status === "active" ? "Replacement key" : "API key"}</span>
            <input
              id={secretId}
              // Write-only: never populated from the server, and cleared the
              // moment the server has it.
              type="password"
              autoComplete="off"
              spellCheck={false}
              maxLength={8192}
              value={form.secret}
              disabled={!keyConfigured || form.submitting}
              placeholder={
                keyConfigured ? "Paste the vendor key" : "Credential storage unavailable"
              }
              onChange={(event) => dispatch({ kind: "edit", secret: event.target.value })}
            />
            <small>
              Stored encrypted and never shown again. This console cannot read a stored key back.
            </small>
          </label>
          <button
            className="button button--primary"
            type="submit"
            disabled={!keyConfigured || form.submitting || form.secret.length === 0}
          >
            <KeyRound aria-hidden="true" size={15} />
            {form.submitting ? "Saving" : provider.status === "active" ? "Rotate key" : "Save key"}
          </button>
        </form>
      ) : null}

      {form.notice ? (
        <p className="provider-card__notice" role="status">
          <ShieldCheck aria-hidden="true" size={15} />
          {form.notice}
        </p>
      ) : null}
      {form.error ? (
        <div className="inline-alert inline-alert--error" role="alert">
          <CircleAlert aria-hidden="true" size={16} />
          <span>{form.error}</span>
        </div>
      ) : null}

      {testable ? (
        <div className="provider-card__test">
          <label className="form-field" htmlFor={modelId}>
            <span>Model id</span>
            <input
              id={modelId}
              value={model}
              spellCheck={false}
              maxLength={256}
              placeholder="the vendor's own model identifier"
              onChange={(event) => setModel(event.target.value)}
            />
          </label>
          <label className="form-field" htmlFor={baseUrlId}>
            <span>Base URL override</span>
            <input
              id={baseUrlId}
              value={baseUrl}
              spellCheck={false}
              maxLength={2048}
              placeholder={provider.baseUrl}
              onChange={(event) => setBaseUrl(event.target.value)}
            />
          </label>
          <button
            className="button"
            type="button"
            disabled={testing || model.trim().length === 0}
            onClick={() => void runTest()}
          >
            <Plug aria-hidden="true" size={15} />
            {testing ? "Testing" : "Test connection"}
          </button>
          <small className="cell-secondary provider-card__test-note">
            Used for this test only. Neither the model id nor the base URL is stored — both are sent
            per request, and the provider's answer is discarded rather than recorded.
          </small>
        </div>
      ) : null}

      {testResult ? (
        <p
          className={testResult.ok ? "provider-card__notice" : "inline-alert inline-alert--error"}
          role="status"
        >
          {testResult.ok ? (
            <>
              <ShieldCheck aria-hidden="true" size={15} />
              <span>The provider answered. This credential works.</span>
            </>
          ) : (
            <>
              <CircleAlert aria-hidden="true" size={15} />
              <span>
                {titleCase(testResult.outcome)}
                {testResult.reason ? `: ${testResult.reason}` : "."}
              </span>
            </>
          )}
        </p>
      ) : null}

      {actionError ? (
        <div className="inline-alert inline-alert--error" role="alert">
          <CircleAlert aria-hidden="true" size={16} />
          <span>{actionError}</span>
        </div>
      ) : null}

      {provider.status === "active" && !credentialless ? (
        confirmingRevoke ? (
          // The control that calls the API exists only once the operator has
          // armed it. Revocation destroys the stored material and there is no
          // way back except supplying a different key, so one click must not
          // reach it.
          <div
            className="provider-card__confirm"
            role="alertdialog"
            aria-label="Confirm revocation"
          >
            <AlertTriangle aria-hidden="true" size={16} />
            <div>
              <strong>Revoke this credential?</strong>
              <p>
                The stored secret is destroyed and cannot be recovered. Restoring access means
                obtaining a new key from the vendor and configuring it here.
              </p>
            </div>
            <div className="provider-card__confirm-actions">
              <button className="button button--small" type="button" onClick={onCancelRevoke}>
                Cancel
              </button>
              <button
                className="button button--small button--danger"
                type="button"
                onClick={() => void confirmRevoke()}
              >
                <Trash2 aria-hidden="true" size={13} />
                Revoke permanently
              </button>
            </div>
          </div>
        ) : (
          <div className="provider-card__actions">
            <button className="text-button" type="button" onClick={onArmRevoke}>
              Revoke credential
            </button>
          </div>
        )
      ) : null}
    </article>
  );
}

// --- the page ---------------------------------------------------------------

const EGRESS_GROUPS = [
  {
    egress: "local" as const,
    title: "Local — nothing leaves this host",
    description:
      "Runs against a loopback address. Your source code stays inside your boundary; no third party sees it.",
  },
  {
    egress: "remote" as const,
    title: "Remote — your source code is sent to a third party",
    description:
      "Each tenant brings its own provider key. Configuring one of these authorises Provenance to transmit source code outside your boundary to the named vendor.",
  },
];

const PROVIDER_DISPLAY_PRIORITY: Readonly<Record<string, number | undefined>> = Object.freeze({
  openai: 0,
  "openai-oauth": 1,
  anthropic: 2,
  "anthropic-oauth": 3,
  deepseek: 4,
});

function orderProviderCards(items: readonly ProviderView[]): ProviderView[] {
  return [...items].sort((left, right) => {
    const leftPriority = PROVIDER_DISPLAY_PRIORITY[left.id] ?? 100;
    const rightPriority = PROVIDER_DISPLAY_PRIORITY[right.id] ?? 100;
    return leftPriority - rightPriority || left.id.localeCompare(right.id);
  });
}

/**
 * The rendered surface, separated from the fetching so every state it can be in
 * — including the one a non-owner sees — is reachable by rendering it.
 */
export function ProviderSettingsView({
  data,
  error,
  isLoading,
  isRefreshing,
  actions,
}: {
  data: ProviderListing | null;
  error: Error | null;
  isLoading: boolean;
  isRefreshing: boolean;
  actions: ProviderActions;
}) {
  const [armedRevoke, setArmedRevoke] = useState<string | null>(null);

  if (error && isOwnerRequired(error)) {
    return (
      <section className="page" aria-labelledby="provider-settings-heading">
        <div className="page-heading">
          <div>
            <h1 id="provider-settings-heading">Model providers</h1>
            <p>Optional model assistance. Provenance is fully functional with none of it.</p>
          </div>
        </div>
        <OwnerRequiredPanel />
      </section>
    );
  }

  const items = data?.items ?? [];
  const remoteConfigured = items.filter(
    (item) => item.egress === "remote" && item.status === "active",
  ).length;
  // Derived rather than naming Ollama, so the summary stays true if the
  // registry ever gains a second local-egress provider or loses this one.
  const localProviders = items.filter((item) => item.egress === "local");

  return (
    <section className="page" aria-labelledby="provider-settings-heading">
      <div className="page-heading">
        <div>
          <span className="page-kicker">Optional assistance</span>
          <h1 id="provider-settings-heading">Model providers</h1>
          <p>
            Optional model assistance. No provider participates in a verdict, and the product is
            fully functional with none configured.
          </p>
        </div>
        <div className="page-heading__actions">
          <button
            className="icon-button"
            type="button"
            aria-label="Refresh providers"
            title="Refresh providers"
            disabled={isRefreshing}
            onClick={actions.refresh}
          >
            <RefreshCw aria-hidden="true" className={isRefreshing ? "spin" : undefined} size={17} />
          </button>
        </div>
      </div>

      <section className="provider-trust-banner" aria-label="Model assistance explanation">
        <div>
          <span className="page-kicker">Your evidence stays in charge</span>
          <h2>Model assistance can help explain work. It cannot decide the result.</h2>
        </div>
        <p>
          Findings stay tied to source evidence and human decisions. Providers can explain work;
          they cannot decide it.
        </p>
      </section>

      {data ? (
        <section className="summary-line" aria-label="Provider summary">
          <span className={remoteConfigured > 0 ? "text-warning" : undefined}>
            <Globe aria-hidden="true" size={15} />
            <strong>{remoteConfigured}</strong>{" "}
            {remoteConfigured === 1 ? "third-party provider" : "third-party providers"} configured
          </span>
          {localProviders.length > 0 ? (
            <span>
              <HardDrive aria-hidden="true" size={15} />{" "}
              {localProviders.length === 1
                ? `${providerLabel(localProviders[0]?.id ?? "")} runs locally and needs no credential`
                : `${localProviders.length} providers run locally and need no credential`}
            </span>
          ) : null}
        </section>
      ) : null}

      {data && !data.keyConfigured ? (
        <div className="provider-readiness-card" role="status">
          <ShieldCheck aria-hidden="true" size={18} />
          <div>
            <strong>Locked by default: analysis assistance is optional and gated.</strong>
            <p>
              The console can show evidence-bound explanations from sealed artifacts. Live remote
              provider credentials cannot be stored until an operator enables credential custody.
            </p>
          </div>
        </div>
      ) : null}

      {error && !isOwnerRequired(error) ? (
        <InlineError error={error} onRetry={actions.refresh} />
      ) : null}
      {isLoading && !data ? <LoadingTable columns={3} rows={6} /> : null}
      {!isLoading && data && items.length === 0 ? (
        <EmptyState
          title="No providers are available"
          description="This build registers no model providers, so there is nothing to configure."
        />
      ) : null}

      {EGRESS_GROUPS.map((group) => {
        const groupItems = orderProviderCards(items.filter((item) => item.egress === group.egress));
        if (groupItems.length === 0) return null;
        return (
          <section
            className={`provider-group provider-group--${group.egress}`}
            aria-labelledby={`provider-group-${group.egress}`}
            key={group.egress}
          >
            <div className="provider-group__heading">
              <h2 id={`provider-group-${group.egress}`}>{group.title}</h2>
              <p>{group.description}</p>
            </div>
            <div className="provider-grid">
              {groupItems.map((item) => (
                <ProviderCard
                  provider={item}
                  keyConfigured={data?.keyConfigured ?? false}
                  confirmingRevoke={armedRevoke === item.id}
                  onArmRevoke={() => setArmedRevoke(item.id)}
                  onCancelRevoke={() => setArmedRevoke(null)}
                  actions={actions}
                  key={item.id}
                />
              ))}
            </div>
          </section>
        );
      })}
    </section>
  );
}

export function ProviderSettingsPage() {
  const load = useCallback((signal: AbortSignal) => modelApi.listProviders(signal), []);
  const providers = useApiResource(load);
  const { refresh } = providers;

  const actions: ProviderActions = {
    async save(providerId, secret) {
      try {
        const result = await modelApi.setProviderSecret(providerId, secret);
        refresh();
        return { kind: "saved", outcome: result.outcome };
      } catch (reason) {
        // Only the status is read. The rejected request body *is* the secret,
        // so nothing derived from it is reported -- the same rule the route
        // applies when it declines to echo what it received.
        return { kind: "failed", message: saveFailureMessage(reason) };
      }
    },
    async revoke(providerId) {
      await modelApi.revokeProvider(providerId);
      refresh();
    },
    test: (providerId, input) => modelApi.testProvider(providerId, input),
    connectOAuth(providerId) {
      // A full-page navigation: the route answers with a redirect to the
      // vendor's consent screen and sets a state cookie scoped to the callback.
      window.location.assign(oauthAuthorizePath(providerId));
    },
    refresh,
  };

  return (
    <ProviderSettingsView
      data={providers.data}
      error={providers.error}
      isLoading={providers.isLoading}
      isRefreshing={providers.isRefreshing}
      actions={actions}
    />
  );
}
